import { Button } from "@/components/ui/button";
import { Printer } from "@phosphor-icons/react";
import { Loader, MiniTile, useReport, printEODReport, ExportCsvButton } from "../shared";

export const EndOfDaySection = ({ filter }) => {
  const [data, loading] = useReport("/reports/end-of-day", filter, true);
  return (
    <div className="space-y-4" data-testid="eod-section">
      <div className="flex items-center justify-end gap-4">
        <ExportCsvButton path="/reports/end-of-day/export.csv" filter={filter} isSingleDay testId="export-eod-csv" />
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => printEODReport(data)}
          disabled={!data || loading}
          data-testid="eod-print-btn"
        >
          <Printer size={16} /> Print
        </Button>
      </div>
      {loading && <Loader />}
      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniTile label="Net Sales" value={`$${data.total_sales.toFixed(2)}`} testId="eod-sales" />
            <MiniTile label="Tax Collected" value={`$${data.total_tax.toFixed(2)}`} testId="eod-tax" />
            <MiniTile label="Tips" value={`$${data.total_tips.toFixed(2)}`} testId="eod-tips" />
            <MiniTile label="Tickets" value={data.order_count} testId="eod-orders" />
            <MiniTile label="Avg Ticket" value={`$${data.avg_ticket.toFixed(2)}`} testId="eod-avg" />
            <MiniTile
              label="Refunds"
              value={`$${data.total_refunded.toFixed(2)} (${data.refunded_orders})`}
              tone={data.total_refunded > 0 ? "text-amber-700" : ""}
              testId="eod-refunds"
            />
            <MiniTile
              label="Voids"
              value={data.cancelled_orders}
              tone={data.cancelled_orders > 0 ? "text-amber-700" : ""}
              testId="eod-voids"
            />
            <MiniTile
              label="Payment Split"
              value={
                Object.entries(data.payment_split)
                  .map(([k, v]) => `${k}: $${v.toFixed(2)}`)
                  .join(" · ") || "—"
              }
              valueClass="text-sm"
              testId="eod-split"
            />
            {data.cash_discount_total > 0 && (
              <MiniTile
                label="Card Fee Recovered"
                value={`$${data.cash_discount_total.toFixed(2)} (${data.cash_discount_orders})`}
                tone="text-emerald-700"
                testId="eod-cash-discount"
              />
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="px-3 py-2 bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
                By Server
              </div>
              <div className="divide-y divide-border max-h-72 overflow-y-auto">
                {data.by_server.length === 0 && (
                  <div className="px-3 py-3 text-sm text-muted-foreground">No paid orders</div>
                )}
                {data.by_server.map((s) => (
                  <div
                    key={s.name}
                    className="px-3 py-2 flex items-center justify-between text-sm"
                    data-testid={`eod-server-${s.name}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{s.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.orders} orders · ${s.sales.toFixed(2)} sales
                      </div>
                    </div>
                    <div className="font-mono font-semibold text-accent shrink-0">
                      +${s.tips.toFixed(2)} tips
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="px-3 py-2 bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
                Top Categories
              </div>
              <div className="divide-y divide-border max-h-72 overflow-y-auto">
                {data.top_categories.length === 0 && (
                  <div className="px-3 py-3 text-sm text-muted-foreground">—</div>
                )}
                {data.top_categories.map((c) => (
                  <div
                    key={c.name}
                    className="px-3 py-2 flex justify-between text-sm"
                    data-testid={`eod-cat-${c.name}`}
                  >
                    <span className="capitalize">{c.name}</span>
                    <span className="font-mono font-semibold">${c.amount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
